import React, { useState, useEffect } from "react";
import { X } from "lucide-react";
import { useCreateVendor, useUpdateVendor } from "@/hooks/useInventory";
import { VendorFormData, VendorWithOutstanding } from "@/types/inventory";

type VendorFormModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: (message: string) => void;
  onError?: (message: string) => void;
  vendorId?: string;
  initialData?: VendorWithOutstanding;
};

export function VendorFormModal({
  isOpen,
  onClose,
  onSuccess,
  onError,
  vendorId,
  initialData,
}: VendorFormModalProps) {
  const [formData, setFormData] = useState<VendorFormData>({
    name: "",
    company_name: "",
    phone: "",
    email: "",
    address: "",
    default_credit_days: 0,
    notes: "",
    is_active: true,
  });

  // Load existing data when in edit mode
  useEffect(() => {
    if (initialData) {
      setFormData({
        name: initialData.name,
        company_name: initialData.company_name || "",
        phone: initialData.phone || "",
        email: initialData.email || "",
        address: initialData.address || "",
        default_credit_days: initialData.default_credit_days || 0,
        notes: initialData.notes || "",
        is_active: initialData.is_active ?? true,
      });
    }
  }, [initialData]);

  const createVendor = useCreateVendor();
  const updateVendor = useUpdateVendor(vendorId || "");

  const mutation = vendorId ? updateVendor : createVendor;

  const validateEmail = (email: string): boolean => {
    if (!email) return true; // Optional field
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validation
    if (!formData.name || formData.name.trim().length === 0) {
      onError?.("Vendor name is required");
      return;
    }

    if (formData.email && !validateEmail(formData.email)) {
      onError?.("Please enter a valid email address");
      return;
    }

    if (
      formData.default_credit_days !== undefined &&
      formData.default_credit_days < 0
    ) {
      onError?.("Credit days cannot be negative");
      return;
    }

    try {
      // Clean up empty strings - convert to undefined for optional fields
      const cleanedData = {
        ...formData,
        company_name: formData.company_name || undefined,
        phone: formData.phone || undefined,
        email: formData.email || undefined,
        address: formData.address || undefined,
        notes: formData.notes || undefined,
      };

      await mutation.mutateAsync(cleanedData);
      onSuccess?.(
        vendorId ? "Vendor updated successfully" : "Vendor created successfully"
      );
      onClose();
    } catch (error: any) {
      onError?.(
        error.message || `Failed to ${vendorId ? "update" : "create"} vendor`
      );
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
          <h2 className="text-xl font-semibold text-gray-900">
            {vendorId ? "Edit Vendor" : "Add Vendor"}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Vendor Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Vendor Name *
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) =>
                setFormData({ ...formData, name: e.target.value })
              }
              placeholder="e.g., Rajesh Kumar"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#5B8E87] focus:border-transparent"
              required
            />
          </div>

          {/* Company Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Company Name
            </label>
            <input
              type="text"
              value={formData.company_name}
              onChange={(e) =>
                setFormData({ ...formData, company_name: e.target.value })
              }
              placeholder="e.g., Health Supplements India Pvt Ltd"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#5B8E87] focus:border-transparent"
            />
          </div>

          {/* Phone & Email (side by side) */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Phone
              </label>
              <input
                type="tel"
                value={formData.phone}
                onChange={(e) =>
                  setFormData({ ...formData, phone: e.target.value })
                }
                placeholder="e.g., +91 98765 43210"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#5B8E87] focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email
              </label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) =>
                  setFormData({ ...formData, email: e.target.value })
                }
                placeholder="e.g., vendor@example.com"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#5B8E87] focus:border-transparent"
              />
            </div>
          </div>

          {/* Address */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Address
            </label>
            <textarea
              value={formData.address}
              onChange={(e) =>
                setFormData({ ...formData, address: e.target.value })
              }
              rows={2}
              placeholder="Full address..."
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#5B8E87] focus:border-transparent resize-none"
            />
          </div>

          {/* Default Credit Days */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Default Credit Period (days)
            </label>
            <input
              type="number"
              min="0"
              value={formData.default_credit_days || ""}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  default_credit_days: e.target.value
                    ? parseInt(e.target.value)
                    : 0,
                })
              }
              placeholder="0"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#5B8E87] focus:border-transparent"
            />
            <p className="text-xs text-gray-500 mt-1">
              Number of days for payment after purchase (0 = immediate payment)
            </p>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Notes
            </label>
            <textarea
              value={formData.notes}
              onChange={(e) =>
                setFormData({ ...formData, notes: e.target.value })
              }
              rows={3}
              placeholder="Internal notes about this vendor..."
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#5B8E87] focus:border-transparent resize-none"
            />
          </div>

          {/* Active Status */}
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="is_active"
              checked={formData.is_active}
              onChange={(e) =>
                setFormData({ ...formData, is_active: e.target.checked })
              }
              className="w-4 h-4 text-[#5B8E87] border-gray-300 rounded focus:ring-[#5B8E87]"
            />
            <label htmlFor="is_active" className="text-sm text-gray-700">
              Active vendor
            </label>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={mutation.isPending}
              className="flex-1 px-4 py-2 bg-[#5B8E87] text-white rounded-lg hover:bg-[#4A7069] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {mutation.isPending
                ? "Saving..."
                : vendorId
                ? "Update Vendor"
                : "Add Vendor"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
